import { tryGetPointInTimeUtc } from "./pointInTimeNormaliser";

/**
 * Data snapshot (ADR-0006) availability for a single analytics page.
 *
 * UNAVAILABLE states still render the control, disabled with a short tooltip,
 * so the feature does not silently disappear on pages that cannot honour it.
 */
export type PointInTimeAvailability =
  | "AVAILABLE"
  | "LOADING"
  | "UNAVAILABLE-PAGE"
  | "UNAVAILABLE-NO-HISTORY";

export interface PointInTimeFloor {
  earliestSelectableDate: string | null;
  enabled: boolean;
}

/**
 * Usage and Custom reports do not honour point-in-time (ADR-0006). Accepts either the
 * page identifier or the saved-filter page key.
 */
export function pageSupportsPointInTime(
  pageIdentifier: string | null | undefined
): boolean {
  const key = (pageIdentifier ?? "").trim().toUpperCase();
  return (
    key !== "USAGEREPORT" &&
    key !== "USAGE" &&
    key !== "CUSTOMREPORTS" &&
    key !== "CUSTOM"
  );
}

export function resolvePointInTimeAvailability(args: {
  pageIdentifier: string | null | undefined;
  floor: PointInTimeFloor | null;
  floorIsLoading?: boolean;
}): PointInTimeAvailability {
  if (!pageSupportsPointInTime(args.pageIdentifier)) {
    return "UNAVAILABLE-PAGE";
  }
  if (args.floorIsLoading) {
    return "LOADING";
  }
  if (args.floor == null || !args.floor.enabled) {
    return "UNAVAILABLE-NO-HISTORY";
  }
  const floorDate = canonicalDate(args.floor.earliestSelectableDate);
  const ceilingDate = latestSelectablePointInTimeDate();
  // First dimensions sweep can be "today" while the picker ceiling is yesterday.
  if (floorDate != null && floorDate > ceilingDate) {
    return "UNAVAILABLE-NO-HISTORY";
  }
  return "AVAILABLE";
}

export function isPointInTimeUsable(
  availability: PointInTimeAvailability
): boolean {
  return availability === "AVAILABLE";
}

/**
 * Data snapshot switched on with no date chosen yet. There is nothing to run,
 * so Apply / Update / Save as new stay disabled until a date is picked.
 */
export function isPointInTimeSelectionIncomplete(args: {
  availability: PointInTimeAvailability;
  isOn: boolean;
  pointInTimeDate: string | null | undefined;
}): boolean {
  if (!isPointInTimeUsable(args.availability) || !args.isOn) {
    return false;
  }
  return (args.pointInTimeDate?.trim() ?? "") === "";
}

/** Canonical yyyy-MM-dd, or null when empty / not a real calendar date. */
function canonicalDate(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  if (trimmed === "") {
    return null;
  }
  return tryGetPointInTimeUtc(trimmed).ok ? trimmed : null;
}

/** Today as a UTC calendar date (yyyy-MM-dd). */
export function todayPointInTimeDate(now: Date = new Date()): string {
  const year = now.getUTCFullYear();
  const month = `${now.getUTCMonth() + 1}`.padStart(2, "0");
  const day = `${now.getUTCDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Latest date the picker may offer: yesterday UTC.
 * Dimensions lag ~24h, so "today" cannot be honoured yet (ADR-0006).
 */
export function latestSelectablePointInTimeDate(now: Date = new Date()): string {
  const yesterdayUtc = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1)
  );
  return todayPointInTimeDate(yesterdayUtc);
}

/**
 * Keeps a chosen date inside the range the client actually has history for.
 * yyyy-MM-dd sorts lexicographically, so plain string compares are safe here.
 * Ceiling defaults to yesterday UTC (latest selectable day).
 * When the floor is after the ceiling (no honourable day yet), returns null.
 */
export function clampPointInTimeDate(
  pointInTimeDate: string | null | undefined,
  earliestSelectableDate: string | null | undefined,
  latestSelectableDate: string | null | undefined = latestSelectablePointInTimeDate()
): string | null {
  const floor = canonicalDate(earliestSelectableDate);
  const ceiling = canonicalDate(latestSelectableDate);
  if (floor != null && ceiling != null && floor > ceiling) {
    return null;
  }

  const date = canonicalDate(pointInTimeDate);
  if (date == null) {
    return null;
  }
  if (floor != null && date < floor) {
    return floor;
  }
  if (ceiling != null && date > ceiling) {
    return ceiling;
  }
  return date;
}

/**
 * A local Date whose day/month/year match the UTC calendar date, so date-fns
 * `format` cannot shift the label back a day in negative UTC offsets.
 */
export function pointInTimeDateForDisplay(
  pointInTimeDate: string | null | undefined
): Date | null {
  const date = canonicalDate(pointInTimeDate);
  if (date == null) {
    return null;
  }
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year, month - 1, day);
}
